import { useEffect, useState } from "react";
import Layout from "../../layout.tsx/app";
import { useOfflineAsesmenStore } from "../offline-asesmen.store";
import { Button } from "../../../components/buttons";
import BaseModal from "../../../components/modal/base";
import { FormProvider, useForm } from "react-hook-form";
import { AssessmentLocation, District } from "../offline-asesmen";
import { useSession } from "../../../stores/session";
import { request } from "../../../api/config";
import { useAlert } from "../../../stores/alert";
import { FormSelectAsync } from "../../../components/forms/input-select";
import { FormTextArea } from "../../../components/forms/input-textarea";
import { FormInput } from "../../../components/forms/input";
import { Spinner } from "flowbite-react";
import { twMerge } from "tailwind-merge";
import { Pencil } from "@phosphor-icons/react";

type FormValues = {
  district_id: District | null;
  postal_code: string;
  address: string;
  gmaps_link: string;
  lat: string;
  lng: string;
  phone: string;
  notes: string;
};

type ErrorForm = {
  psikolog_id: [] | null;
  district_id: [] | null;
  postal_code: [] | null;
  address: [] | null;
  gmaps_link: [] | null;
  lat: [] | null;
  lng: [] | null;
  phone: [] | null;
  notes: [] | null;
};

const AsesmenSetting = () => {
  const [page, setPage] = useState<number>(1);
  const [modal, setModal] = useState<boolean>(false);
  const [modalMode, setModalMode] = useState<"create" | "edit" | undefined>(
    undefined
  );
  const [selected, setSelected] = useState<AssessmentLocation | undefined>();
  const [loadingSubmit, setLoadingSubmit] = useState<boolean>(false);
  const [errors, setErrors] = useState<ErrorForm | null>(null);

  const { me } = useSession();
  const { setMessage } = useAlert();

  const forms = useForm<FormValues>();
  const {
    locations,
    loadingLocations,
    getLocations,
    ownLocation,
    getOwnLocation,
    loadingOwnLocation,
  } = useOfflineAsesmenStore();

  const selectRegion = async (inputValue: string) => {
    let params = {
      psikolog_id: me?.id ?? "",
      q: inputValue,
    };
    const { data } = await request.get("/select/district-by-psikolog-region", {
      params: params,
    });
    return data.data;
  };

  const handleFormEdit = (item: AssessmentLocation) => {
    forms.setValue("district_id", item.district_id);
    forms.setValue("address", item.address);
    forms.setValue("notes", item.notes ?? "");
    forms.setValue("gmaps_link", item.gmaps_link ?? "");
    forms.setValue("phone", item.phone ?? "");
  };

  const handleSave = forms.handleSubmit(async (data) => {
    setLoadingSubmit(true);
    try {
      if (modalMode === "create") {
        await request.post("/offline-assessment/place", {
          ...data,
          district_id: data.district_id?.id,
        });
      }

      if (modalMode === "edit") {
        selected &&
          (await request.put(`/offline-assessment/place/${selected.id}`, {
            ...data,
            district_id: data.district_id?.id,
          }));
      }
      setMessage("Data berhasil disimpan", "success");
      getLocations();
      forms.reset({
        district_id: null,
        address: "",
        notes: "",
        gmaps_link: "",
        phone: "",
      });
      setModal(false);
    } catch (err: any) {
      setErrors(err.response.data.errors);
      setMessage("Oops, ada yang salah di server", "error");
    } finally {
      setLoadingSubmit(false);
    }
  });

  useEffect(() => {
    getLocations({ page });
  }, [getLocations, page]);

  useEffect(() => {
    getOwnLocation();
  }, [getOwnLocation]);

  return (
    <>
      <Layout
        withPageTitle={true}
        title={
          <>
            <span>Lokasi Asesmen Tatap Muka</span>
            {loadingOwnLocation ? (
              <>
                <div className="w-[200px] h-6 bg-gray-200 rounded-lg animate-pulse"></div>
              </>
            ) : (
              <small className="block text-base">
                Regional Anda: {ownLocation?.name}
              </small>
            )}
          </>
        }
        pageTitleContent={
          <Button
            onClick={() => {
              setModal(true);
              setModalMode("create");
            }}
            className="px-6 text-sm"
          >
            Tambah Lokasi
          </Button>
        }
      >
        <div className="w-full">
          {loadingLocations ? (
            <div className="w-full h-[20vh] flex items-center justify-center">
              <Spinner />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {locations?.data.map((item, key) => (
                <div
                  key={key}
                  className="bg-gray-50 px-4 py-2 rounded-lg relative"
                >
                  <div
                    className="absolute -top-1 -right-1 bg-blue-600 p-2 cursor-pointer hover:bg-blue-700 rounded-full"
                    onClick={() => {
                      handleFormEdit(item);
                      setSelected(item);
                      setModal(true);
                      setModalMode("edit");
                    }}
                  >
                    <Pencil className="text-white" />
                  </div>
                  <strong>{`${item.district_id.name}`}</strong>
                  <small className="block text-sm">{`${item.district_id?.city?.name} / ${item.district_id?.city?.province?.name}`}</small>
                  <div
                    className={twMerge(
                      "text-xs absolute mt-1",
                      item.approved_at
                        ? "bg-green-50 text-green-600"
                        : "bg-yellow-50 text-yellow-600"
                    )}
                  >
                    {item.approved_at ? "Approved" : "Pending Approval"}
                  </div>
                  <div className="mt-3 border-t pt-3">
                    <small className="block">{item.address}</small>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <BaseModal
          title={
            modalMode === "create"
              ? "Tambah Lokasi Asesmen Offline"
              : "Edit Lokasi Asesmen Offline"
          }
          isOpen={modal}
          close={() => setModal(false)}
        >
          <FormProvider {...forms}>
            <form>
              <FormSelectAsync
                label="Pilih Kecamatan"
                name="district_id"
                control={forms.control}
                loadOption={selectRegion}
                required
                optionLabel={(option: District) => (
                  <>
                    <strong>{option.name}</strong>
                    <small className="block leading-none">{`${
                      option.city?.name ?? "-"
                    } / ${option.city?.province?.name ?? "-"}`}</small>
                  </>
                )}
                optionValue={(option: District) => `${option.id}`}
                error={errors?.psikolog_id}
              />

              <FormTextArea
                name="address"
                control={forms.control}
                label="Alamat"
                error={errors?.address}
                required
              />

              <FormInput
                name="gmaps_link"
                control={forms.control}
                label="Link / URL Google Maps"
                error={errors?.gmaps_link}
              />

              <FormInput
                name="phone"
                control={forms.control}
                label="Nomor HP/Telp"
                error={errors?.phone}
              />

              <FormInput
                name="notes"
                control={forms.control}
                label="Catatan"
                error={errors?.notes}
              />

              <div className="mt-3 flex items-center justify-end">
                <Button className="px-8" onClick={handleSave}>
                  {loadingSubmit ? <Spinner /> : "Simpan"}
                </Button>
              </div>
            </form>
          </FormProvider>
        </BaseModal>
      </Layout>
    </>
  );
};

export default AsesmenSetting;
